import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { TopDiverseVendor } from '@/hooks/useDiversityAnalytics';
import { CertificationDisplayBadge } from './CertificationMultiSelect';
import { cn } from '@/lib/utils';

interface TopDiverseVendorsTableProps {
  data: TopDiverseVendor[] | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

type SortField = 'vendorName' | 'totalSpendCents' | 'jobsAwarded' | 'winRate' | 'avgJobValueCents' | 'percentageOfDiverseSpend';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 20;

export function TopDiverseVendorsTable({ data, isLoading }: TopDiverseVendorsTableProps) {
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('totalSpendCents');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Diverse Vendors</CardTitle>
          <CardDescription>
            Most-used diverse vendors and their performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[200px] items-center justify-center text-muted-foreground">
            No diverse vendor data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort data
  const sortedData = [...data].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];

    if (sortField === 'vendorName') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  // Paginate
  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const paginatedData = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    return (
      <TableHead
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : (
              <ArrowDown className="h-4 w-4" />
            )
          ) : (
            <ArrowUpDown className="h-4 w-4 opacity-50" />
          )}
        </div>
      </TableHead>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Diverse Vendors</CardTitle>
        <CardDescription>
          Most-used diverse vendors and their performance ({data.length} vendors)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader field="vendorName">Vendor Name</SortHeader>
                <TableHead>Certifications</TableHead>
                <SortHeader field="jobsAwarded">Jobs</SortHeader>
                <SortHeader field="totalSpendCents">Total Spend</SortHeader>
                <SortHeader field="percentageOfDiverseSpend">% of Diversity</SortHeader>
                <SortHeader field="winRate">Win Rate</SortHeader>
                <SortHeader field="avgJobValueCents">Avg Job Value</SortHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((vendor) => (
                <TableRow key={vendor.vendorId}>
                  <TableCell className="font-medium">{vendor.vendorName}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {vendor.certifications.slice(0, 3).map((cert) => (
                        <CertificationDisplayBadge key={cert} name={cert} />
                      ))}
                      {vendor.certifications.length > 3 && (
                        <span className="text-xs text-muted-foreground">
                          +{vendor.certifications.length - 3} more
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{vendor.jobsAwarded}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(vendor.totalSpendCents)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercentage(vendor.percentageOfDiverseSpend)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={cn(
                      vendor.winRate >= 50 ? 'text-emerald-600' : vendor.winRate >= 25 ? 'text-amber-600' : ''
                    )}>
                      {formatPercentage(vendor.winRate)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(vendor.avgJobValueCents)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, data.length)} of {data.length} vendors
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
